import { Box, Button, TextField } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import styles from './Job.module.scss';
import classNames from 'classnames/bind';
import { PlusIcon, SearchIcon, ViewBoardIcon, TrashIcon, ViewListIcon } from '~/assets/icons';
import { BaseHeaderTitle, BaseSelect, BaseTab, BaseTabList } from '~/components/common';
import { useTranslation } from 'react-i18next';
import JobList from './components/JobList';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { jobActions } from '~/thunks/job/jobSlice';
import useDebounce from '~/hooks/useDebounce';
import JobBoard from './components/JobBoard';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IJob, IPayloadDeleteJobs } from '~/utils/interface/job';
import jobApi from '~/apis/job';
import { cast, catchError, downloadCSV, handleApiError, notify } from '~/utils/helper';
import { handleGetJob, handleGetJobs, handleGetJobsBoard } from '~/thunks/job/jobThunk';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditJobDialog from '~/components/jobs/edit-job-dialog/EditJobDialog';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import {
  MASTER_DATA_JOB_CATEGORIES,
  MASTER_DATA_JOB_TYPES,
  MASTER_DATA_CUSTOMERS,
  MASTER_DATA_PRICES,
  MASTER_DATA_ALL_CUSTOMERS,
  MASTER_DATA_REFERRAL_SOURCES,
  MASTER_DATA_JOB_PROPERTIES,
  MASTER_DATA_CUSTOMER_STATUSES,
  MASTER_DATA_SOURCE_OF_LOSSES,
  MASTER_DATA_WATER_CATEGORY,
  MASTER_DATA_WATER_CLASS,
  MASTER_DATA_HAZARD,
  MASTER_DATA_ACCESS_CODE,
  MASTER_DATA_CUSTOMER_TYPE,
  MASTER_DATA_JOB_STATUSES,
} from '~/utils/constants/master';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import dayjs from 'dayjs';
import { StatusModeInfo } from '~/utils/types/job';

const cx = classNames.bind(styles);

type ViewModeState = 'list' | 'board';

const Job = () => {
  const [viewMode, setViewMode] = useState<ViewModeState>('list');
  const [mode, setMode] = useState<'full' | 'me'>('full');
  const [searchInput, setSearchInput] = useState<string>('');
  const searchInputDebounce = useDebounce<string>(searchInput);

  const { selectedIds, openAlertDelete, menuSelectedId, filter, needRefreshData, filterBoard } = useAppSelector(
    (state) => state.job
  );
  const { jobStatuses } = useAppSelector((state) => state.master);
  const { jobTypes } = useAppSelector((state) => state.master);
  const { selectionAccounts } = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/job', 'admin/component']);
  const [openEditJobDialog, setOpenEditJobDialog] = useState<boolean>(false);
  const [editJobDialogMode, setEditJobDialogMode] = useState<string>('');
  const [editData, setEditData] = useState<IJob>();
  const permissions = usePermission();
  const optionsWithAll = [{ id: '', name: 'All', code: '' }, ...(jobTypes ?? [])];
  const optionsWithAllStatus = [{ id: 'all', name: 'All' }, ...(jobStatuses ?? [])];
  const [selectedStatus, setSelectedStatus] = useState<Array<string>>([]);

  useEffect(() => {
    // Prevent filter run twice times
    if (dispatch && typeof searchInputDebounce !== 'undefined' && filter.search !== searchInputDebounce) {
      dispatch(
        jobActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name', 'customer'],
          page: 1,
        })
      );

      dispatch(
        jobActions.setFilterBoard({
          search: searchInputDebounce,
          searchFields: ['name', 'customer'],
        })
      );
    }
  }, [searchInputDebounce, filter, dispatch]);

  useEffect(() => {
    if (viewMode === 'list') {
      dispatch(
        handleGetJobs({
          ...filter,
          status: selectedStatus.includes('all') ? [] : (selectedStatus as Array<StatusModeInfo>),
        })
      )
        .unwrap()
        .catch((e) => {
          handleApiError(e);
        });
    } else {
      dispatch(
        handleGetJobsBoard({
          ...filterBoard,
          status: selectedStatus.includes('all') ? [] : (selectedStatus as Array<StatusModeInfo>),
        })
      )
        .unwrap()
        .catch((e) => {
          handleApiError(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, viewMode, needRefreshData, selectedStatus]);

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_JOB_TYPES, MASTER_DATA_JOB_STATUSES],
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const openAlertDeleteModal = useCallback(() => {
    dispatch(jobActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(jobActions.changeMenuSelectId(undefined));
    dispatch(jobActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedJobs = useCallback(async () => {
    let payloadDeleteJobs: IPayloadDeleteJobs =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await jobApi.deleteJobs(payloadDeleteJobs);

      notify('success', t('deleteSuccessMessage'));

      dispatch(jobActions.changeNeedRefreshData(true));
      dispatch(jobActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  const handleChangeViewMode = (_event: React.ChangeEvent<{}>, newValue: ViewModeState) => {
    setViewMode(newValue);
    dispatch(
      jobActions.setFilter({
        page: 1,
      })
    );
  };

  const handleChangeMode = (_event: React.ChangeEvent<{}>, newValue: 'full' | 'me') => {
    setMode(newValue);
    dispatch(
      jobActions.setFilter({
        mode: newValue,
      })
    );
    dispatch(
      jobActions.setFilterBoard({
        mode: newValue,
      })
    );
  };

  const fetchMasterData = async () => {
    try {
      await dispatch(
        handleGetMasterData({
          type: [
            MASTER_DATA_JOB_TYPES,
            MASTER_DATA_JOB_CATEGORIES,
            MASTER_DATA_CUSTOMERS,
            MASTER_DATA_CUSTOMER_TYPE,
            MASTER_DATA_PRICES,
            MASTER_DATA_ALL_CUSTOMERS,
            MASTER_DATA_REFERRAL_SOURCES,
            MASTER_DATA_JOB_PROPERTIES,
            MASTER_DATA_CUSTOMER_STATUSES,
            MASTER_DATA_SOURCE_OF_LOSSES,
            MASTER_DATA_WATER_CATEGORY,
            MASTER_DATA_WATER_CLASS,
            MASTER_DATA_HAZARD,
            MASTER_DATA_ACCESS_CODE,
          ],
        })
      ).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleClickCreateJob = async () => {
    await fetchMasterData();
    setEditJobDialogMode(DIALOG_MODE_CREATE);
    setOpenEditJobDialog(true);
  };

  const handleOpenEdit = async (id: string) => {
    try {
      await fetchMasterData();
      const response = await dispatch(handleGetJob(id)).unwrap();
      setEditData(response);
      setEditJobDialogMode(DIALOG_MODE_EDIT);
      setOpenEditJobDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setEditData(undefined);
    setOpenEditJobDialog(open);
    if (needRefreshData) dispatch(jobActions.changeNeedRefreshData(true));
  };

  const handleAssigneeChange = (event: { target: { value: string[] } }) => {
    dispatch(jobActions.setFilter({ assigneeIds: event.target.value }));
    dispatch(jobActions.setFilterBoard({ assigneeIds: event.target.value }));
  };

  const handleExport = async () => {
    const now = dayjs().format('YYYY-MM-DD');
    const fileName = `Project_Export_${now}.csv`;
    const res = await jobApi.export(filter);
    downloadCSV(res.data, fileName);
  };

  return (
    <Box className='w-full h-full'>
      <Box className='flex flex-row items-center justify-between'>
        <BaseHeaderTitle title={t('title')} />
        <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
          {permissions[PERMISSIONS.project_view] && (
            <Button className={cx('export_container')} variant='contained' disableElevation onClick={handleExport}>
              {t('export')}
            </Button>
          )}
          <BaseTabList
            value={viewMode}
            onChange={handleChangeViewMode}
            TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
          >
            <BaseTab
              key='list'
              value={'list'}
              label={t('list')}
              iconPosition='start'
              icon={
                viewMode === 'list' ? (
                  <ViewListIcon className='w-[17px] h-[17px]' stroke='#054DFF' />
                ) : (
                  <ViewListIcon className='w-[17px] h-[17px]' />
                )
              }
            />
            <BaseTab
              key='board'
              value={'board'}
              label={t('board')}
              iconPosition='start'
              icon={
                viewMode === 'board' ? (
                  <ViewBoardIcon className='w-[17px] h-[17px]' stroke='#054DFF' />
                ) : (
                  <ViewBoardIcon className='w-[17px] h-[17px]' />
                )
              }
            />
          </BaseTabList>
          {permissions[PERMISSIONS.project_create] && (
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleClickCreateJob}
            >
              {t('addJob')}
            </Button>
          )}
        </Box>
      </Box>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />

            <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
              <Box className='w-[204px]'>
                <BaseSelect
                  name='jobTypeId'
                  placeholder={t('jobType')}
                  value={cast(filter.jobTypeId)}
                  options={optionsWithAll}
                  onChange={(value) => {
                    dispatch(
                      jobActions.setFilter({
                        jobTypeId: value.target.value,
                      })
                    );
                    dispatch(
                      jobActions.setFilterBoard({
                        jobTypeId: value.target.value,
                      })
                    );
                  }}
                ></BaseSelect>
              </Box>

              <Box className='w-[204px]'>
                <BaseSelect
                  name='status'
                  placeholder={t('jobType')}
                  value={selectedStatus}
                  options={optionsWithAllStatus}
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    if (selectedValue.includes('all') && !selectedStatus.includes('all')) {
                      setSelectedStatus(['all']);
                    } else if (selectedValue !== 'all' && selectedStatus.includes('all')) {
                      setSelectedStatus(selectedValue.filter((value: any) => value !== 'all'));
                    } else {
                      setSelectedStatus(selectedValue);
                    }
                  }}
                  multiple
                ></BaseSelect>
              </Box>

              <Box className='w-[204px]'>
                <BaseSelect
                  options={selectionAccounts}
                  value={filter.assigneeIds}
                  onChange={(event) => handleAssigneeChange(event)}
                  label={t('assignees')}
                  placeholder={t('assignees')}
                  multiple
                />
              </Box>
              <Box className='w-[204px]'>
                <BaseTabList
                  value={mode}
                  onChange={handleChangeMode}
                  TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
                >
                  <BaseTab key={'full'} value={'full'} iconPosition='start' label={t('allProjects')} />
                  <BaseTab key={'me'} value={'me'} iconPosition='start' label={t('myProjects')} />
                </BaseTabList>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className='mt-[40px]'>{viewMode === 'list' ? <JobList onOpenEdit={handleOpenEdit} /> : <JobBoard />}</Box>

      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobs}
      />
      <EditJobDialog open={openEditJobDialog} setOpen={handleSetOpen} mode={editJobDialogMode} data={editData} />
    </Box>
  );
};

export default Job;
